
import { Box, Divider, Stack, Typography } from "@mui/material";
import SectionTabs from "../SectionTabs";
import CompositionHeader from "../CompositionHeader";
import { PatientCard, PractitionerCard, OrganizationSection, HospitalBanner } from "../InfoCards";
import EncounterSection from "../EncounterSection";
import DocumentReferenceSection from "../DocumentReferenceSection";
import ObservationsSection from "../ObservationsSection";
import ProceduresSection from "../ProceduresSection";
import { labelSx, valueSx, pillSx, formatDate } from "../styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WellnessRecordView = ({ bundle, setShowPdfPreview }: { bundle: any; setShowPdfPreview: (v: boolean) => void }) => {
    const composition = bundle?.composition || {};
    const patient = bundle || {};
    const previousMeds = bundle?.previousMedications || [];
    const conditions = bundle?.conditions || [];
    const allergies = bundle?.allergyIntolerances || [];
    const procedures = bundle?.procedures || [];
    const observations = bundle?.observations || [];
    const immunizations = bundle?.processedImmunization || [];

    const sections = [
        {
            id: "medications",
            label: "Medications",
            content:
                previousMeds.length === 0 ? (
                    <Typography sx={labelSx}>No previous medications recorded.</Typography>
                ) : (
                    <Stack spacing={2}>
                        {previousMeds.map((med: any, idx: number) => (
                            <Box
                                key={`${med.medicationName || "med"}-${idx}`}
                                sx={{
                                    p: 2.5,
                                    border: "2px solid #e8f4f2",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                    <Typography sx={{ ...valueSx, fontSize: 16 }}>{med.medicationName || "Medication"}</Typography>
                                    {med.medicationStatus ? <Box sx={pillSx}>{med.medicationStatus}</Box> : null}
                                </Stack>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Dosage</Typography>
                                        <Typography sx={valueSx}>{med.dosageInstruction || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Route</Typography>
                                        <Typography sx={valueSx}>{med.dosageRoute || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Frequency</Typography>
                                        <Typography sx={valueSx}>{`${med.dosageFrequency || "-"} x ${med.dosagePeriod || "-"} ${med.dosagePeriodUnit || ""}`}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Recorded On</Typography>
                                        <Typography sx={valueSx}>{formatDate(med.datePrescribed || med.recordedDate)}</Typography>
                                    </Box>
                                    {med.requester ? (
                                        <Box sx={{ display: "flex", flexDirection: "column", gridColumn: { xs: "1", sm: "1 / 2", lg: "1 / 2" } }}>
                                            <Typography sx={labelSx}>Requester</Typography>
                                            <Typography sx={valueSx}>{med.requester}</Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        { id: "procedures", label: "Procedures", content: <ProceduresSection procedures={procedures} /> },
        {
            id: "conditions",
            label: "Conditions",
            content:
                conditions.length === 0 ? (
                    <Typography sx={labelSx}>No conditions captured.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {conditions.map((cond: any, idx: number) => (
                            <Box
                                key={`condition-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={valueSx}>{cond.conditionText || "Condition"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Type</Typography>
                                        <Typography sx={valueSx}>{cond.conditionType || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Recorded</Typography>
                                        <Typography sx={valueSx}>{formatDate(cond.recordedDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "allergies",
            label: "Allergies",
            content:
                allergies.length === 0 ? (
                    <Typography sx={labelSx}>No allergies recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {allergies.map((allergy: any, idx: number) => (
                            <Box
                                key={`allergy-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={valueSx}>{allergy.allergyDisplay || "Allergy"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Clinical Status</Typography>
                                        <Typography sx={valueSx}>{allergy.clinicalStatus || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Verification Status</Typography>
                                        <Typography sx={valueSx}>{allergy.verificationStatus || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Authored</Typography>
                                        <Typography sx={valueSx}>{formatDate(allergy.authoredDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        { id: "investigations", label: "Investigations", content: <ObservationsSection observations={observations} /> },
        {
            id: "immunizations",
            label: "Immunizations",
            content:
                Array.isArray(immunizations) && immunizations.length > 0 ? (
                    <Stack spacing={1.5}>
                        {immunizations.map((imm: any, idx: number) => (
                            <Box
                                key={`immunization-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={valueSx}>{imm.vaccineCodeText || imm.vaccineCode || "Immunization"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Status</Typography>
                                        <Typography sx={valueSx}>{imm.status || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Date</Typography>
                                        <Typography sx={valueSx}>{formatDate(imm.occurrenceDateTime)}</Typography>
                                    </Box>
                                    {imm.routeText ? (
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography sx={labelSx}>Route</Typography>
                                            <Typography sx={valueSx}>{imm.routeText}</Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography sx={labelSx}>No immunizations recorded.</Typography>
                )
        },
        {
            id: "attachments",
            label: "Attachments",
            content: bundle?.documentReference || bundle?.binary?.data ? (
                <DocumentReferenceSection
                    documentReference={bundle?.documentReference}
                    binary={bundle?.binary}
                    onPreviewBase64={() => setShowPdfPreview(true)}
                />
            ) : (
                <Typography sx={labelSx}>No attachments available.</Typography>
            )
        }
    ];

    return (
        <Stack spacing={3}>
            <HospitalBanner organization={bundle?.organization} />
            <CompositionHeader composition={composition} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                <PatientCard patient={patient} />
                <PractitionerCard practitioner={patient} />
            </Stack>

            <OrganizationSection organization={bundle?.organization} />
            <EncounterSection encounter={bundle?.encounter} />

            <Divider />
            <SectionTabs sections={sections} initialId="medications" />
        </Stack>
    );
};

export default WellnessRecordView;
